import axios from "axios";
import { Storage } from "@/Services/Storage";

const TALLY_ADDON_ID = 7;

export default {
  namespaced: true,
  state: () => ({
    storeAddons: [],
    addonsLoading: false,
    addonsError: null,
  }),
  getters: {
    storeAddons: (s) => s.storeAddons,
    addonsLoading: (s) => s.addonsLoading,
    addonsError: (s) => s.addonsError,
    hasTallyAddon: (s) => Array.isArray(s.storeAddons) && s.storeAddons.some(a => Number(a?.id) === TALLY_ADDON_ID),
  },
  mutations: {
    SET_ADDONS(state, list) { state.storeAddons = Array.isArray(list) ? list : [] },
    SET_ADDONS_LOADING(state, v) { state.addonsLoading = !!v },
    SET_ADDONS_ERROR(state, e) { state.addonsError = e || null },
  },
  actions: {
    /**
     * Fetch addons for the current store by CustomerID.
     * Uses window.subscriptionTomcatApi and current store from Storage.
     * Returns an array (may be empty on error), never throws.
     */
    async fetchStoreAddons({ commit }) {
      commit('SET_ADDONS_LOADING', true);
      commit('SET_ADDONS_ERROR', null);
      try {
        const store = await Storage.getCurrentStore();
        const customerId = store?.CustomerID || store?.customerId || store?.customerID;
        if (!customerId) {
          commit('SET_ADDONS', []);
          return [];
        }
        const url = subscriptionTomcatApi + `addons/store/${encodeURIComponent(customerId)}`;
        const { data } = await axios.get(url, { headers: { Accept: 'application/json' } });
        const list = Array.isArray(data) ? data : [];
        commit('SET_ADDONS', list);
        return list;
      } catch (e) {
        // errorHandler is globally available via window (GlobalVariables.js)
        try { errorHandler.tomcatError?.(e); } catch (_) {}
        commit('SET_ADDONS_ERROR', e?.message || 'Failed to load store addons');
        commit('SET_ADDONS', []);
        return [];
      } finally {
        commit('SET_ADDONS_LOADING', false);
      }
    },

    /**
     * Check if a given invoice contains addon 5.
     * All SubscriptionManager API calls must use global prefix and axios.
     * @param {Object} _ - Vuex context (unused)
     * @param {string} invoiceNumber - The invoice number to check
     * @returns {Promise<boolean>} true if contains addon, else false
     */
    async containsAddonOnInvoice(_, invoiceNumber) {
      if (!invoiceNumber) return false;
      try {
        const { data } = await axios.get(
          subscriptionTomcatApi + `addons/invoice/${encodeURIComponent(invoiceNumber)}/contains/5`
        );
        return !!(data && data.containsAddon === true);
      } catch (err) {
        try { errorHandler.tomcatError?.(err); } catch (_) {}
        return false;
      }
    },

    /**
     * Check if a given invoice contains a specific addon ID.
     * All SubscriptionManager API calls must use global prefix and axios.
     * @param {Object} _ - Vuex context (unused)
     * @param {{ invoiceNumber: string, addonId: number }} payload
     * @returns {Promise<boolean>} true if contains addon, else false
     */
    async containsAddonOnInvoiceById(_, payload) {
      const invoiceNumber = payload && payload.invoiceNumber;
      const addonId = payload && payload.addonId;
      if (!invoiceNumber || !addonId) return false;
      try {
        const { data } = await axios.get(
          subscriptionTomcatApi + `addons/invoice/${encodeURIComponent(invoiceNumber)}/contains/${encodeURIComponent(addonId)}`
        );
        return !!(data && data.containsAddon === true);
      } catch (err) {
        try { errorHandler.tomcatError?.(err); } catch (_) {}
        return false;
      }
    },

    /**
     * Activate addon for a store.
     * @param {Object} _ - Vuex context (unused)
     * @param {{storeId: string, addonId?: number}} payload - storeId and addonId (defaults to 1)
     * @returns {Promise<boolean>} true on success
     */
    async activateAddonForStore(_, payload) {
      const storeId = payload && payload.storeId;
      const addonId = (payload && payload.addonId) || 1;
      if (!storeId) return false;
      try {
        await axios.post(
          subscriptionTomcatApi + `addons/store/${encodeURIComponent(storeId)}/addon/${encodeURIComponent(addonId)}`
        );
        return true;
      } catch (err) {
        try { errorHandler.tomcatError?.(err); } catch (_) {}
        return false;
      }
    }
  }
};
